// ============================================
// USERS ADMIN PAGE
// Super Admin only — invite, edit role, disable, delete.
// ============================================

import React, { useState, useEffect } from "react";
import { useTheme } from "../components/ThemeContext";
import { useAuth, ROLE_COLORS, ROLE_LABELS, ROLES } from "../components/Auth";
import { getUsers, inviteUser, updateUser, deleteUser, resendInvite } from "../services/api";

const StatusBadge = ({ status, C }) => {
  const colors = { active: C.green, pending: C.orange, disabled: C.red };
  const labels = { active: "Active", pending: "Pending", disabled: "Disabled" };
  const c = colors[status] || C.muted;
  return (
    <span style={{ padding:"2px 9px", background:`${c}22`, color:c, border:`1px solid ${c}44`, borderRadius:6, fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.4px" }}>
      {labels[status] || status}
    </span>
  );
};

const fmt = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("en-US", { month:"short", day:"numeric", year:"numeric", hour:"numeric", minute:"2-digit" }); } catch { return d; }
};

export default function UsersAdmin() {
  const { C }    = useTheme();
  const { user } = useAuth();
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [msg, setMsg]           = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await getUsers(); setUsers(r.data.users||[]); }
    catch(err) { setMsg({ type:"error", text: err.response?.data?.message || "Failed to load users" }); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const flash = (m) => { setMsg(m); setTimeout(()=>setMsg(null), 4000); };

  const handleInvite = async (email, role) => {
    try {
      const r = await inviteUser(email, role);
      setInviteResult({ ...r.data, email, role });
      load();
      return true;
    } catch (err) {
      flash({ type:"error", text: err.response?.data?.message || "Invitation failed" });
      return false;
    }
  };

  const handleDisable = async (u) => {
    if (!window.confirm(`Disable ${u.name}? They won't be able to log in until you re-enable them.`)) return;
    try { await updateUser(u.id, { status:"disabled" }); flash({ type:"success", text:`${u.name} disabled` }); load(); }
    catch(err) { flash({ type:"error", text: err.response?.data?.message || "Failed" }); }
  };

  const handleEnable = async (u) => {
    try { await updateUser(u.id, { status:"active" }); flash({ type:"success", text:`${u.name} enabled` }); load(); }
    catch(err) { flash({ type:"error", text: err.response?.data?.message || "Failed" }); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name}?\n\nThis cannot be undone.`)) return;
    try { await deleteUser(u.id); flash({ type:"success", text:`${u.name} deleted` }); load(); }
    catch(err) { flash({ type:"error", text: err.response?.data?.message || "Failed" }); }
  };

  const handleResend = async (u) => {
    try {
      const r = await resendInvite(u.id);
      setInviteResult({ ...r.data, email: u.email, role: u.role });
      load();
    } catch(err) { flash({ type:"error", text: err.response?.data?.message || "Failed" }); }
  };

  return (
    <div>
      {showInvite && <InviteModal C={C} onClose={()=>setShowInvite(false)} onInvite={handleInvite} />}
      {editing && <EditModal user={editing} currentUserId={user.id} C={C} onClose={()=>setEditing(null)}
        onSave={async (data) => {
          try { await updateUser(editing.id, data); flash({ type:"success", text:"Saved" }); setEditing(null); load(); }
          catch(err) { flash({ type:"error", text: err.response?.data?.message || "Failed" }); }
        }} />}
      {inviteResult && <InviteResultModal data={inviteResult} C={C} onClose={()=>setInviteResult(null)} />}

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, color:C.text, fontFamily:"Georgia, serif" }}>Users</div>
          <div style={{ fontSize:13, color:C.muted, marginTop:4 }}>Invite people, assign roles and manage access.</div>
        </div>
        <button onClick={()=>setShowInvite(true)}
          style={{ padding:"10px 22px", background:C.accent, border:"none", borderRadius:10, color:"#000", fontSize:13, fontWeight:700, cursor:"pointer" }}>
          + Invite User
        </button>
      </div>

      {msg && (
        <div style={{ padding:"10px 14px", borderRadius:9, marginBottom:14,
          background: msg.type==="success" ? C.greenSoft : C.redSoft,
          border: `1px solid ${msg.type==="success" ? C.green : C.red}44`,
          color: msg.type==="success" ? C.green : C.red, fontSize:13, fontWeight:600 }}>
          {msg.text}
        </div>
      )}

      {loading ? (
        <div style={{ padding:40, textAlign:"center", color:C.muted }}>Loading users…</div>
      ) : (
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, overflow:"hidden" }}>
          <div className="responsive-table">
            <table>
              <thead>
                <tr style={{ background:C.bg }}>
                  {["User","Email","Role","Status","Last Login","Created","Actions"].map(h=>(
                    <th key={h} style={{ padding:"11px 14px", textAlign:"left", fontSize:10, fontWeight:700, color:C.muted, letterSpacing:"0.6px", textTransform:"uppercase", borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u,i) => {
                  const isMe = u.id === user.id;
                  return (
                    <tr key={u.id} style={{ borderBottom:`1px solid ${C.border}` }}
                      onMouseEnter={e=>e.currentTarget.style.background=C.border+"33"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{ padding:"12px 14px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <div style={{ width:34, height:34, borderRadius:"50%", background:ROLE_COLORS[u.role]||C.accent, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, color:"#000", flexShrink:0 }}>
                            {u.avatar || "?"}
                          </div>
                          <div>
                            <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{u.name} {isMe && <span style={{color:C.muted,fontWeight:500,fontSize:11}}>(you)</span>}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding:"12px 14px", fontSize:12, color:C.muted, fontFamily:"monospace" }}>{u.email}</td>
                      <td style={{ padding:"12px 14px" }}>
                        <span style={{ padding:"2px 9px", background:`${ROLE_COLORS[u.role]}22`, color:ROLE_COLORS[u.role], border:`1px solid ${ROLE_COLORS[u.role]}44`, borderRadius:6, fontSize:11, fontWeight:700 }}>
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      </td>
                      <td style={{ padding:"12px 14px" }}><StatusBadge status={u.status} C={C} /></td>
                      <td style={{ padding:"12px 14px", fontSize:11, color:C.muted }}>{fmt(u.last_login_at)}</td>
                      <td style={{ padding:"12px 14px", fontSize:11, color:C.muted }}>{fmt(u.created_at)}</td>
                      <td style={{ padding:"12px 14px", whiteSpace:"nowrap" }}>
                        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                          {!isMe && (
                            <button onClick={()=>setEditing(u)}
                              style={{ padding:"5px 10px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:6, color:C.accent, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                              Edit
                            </button>
                          )}
                          {u.status === "pending" && (
                            <button onClick={()=>handleResend(u)}
                              style={{ padding:"5px 10px", background:C.orangeSoft, border:`1px solid ${C.orange}44`, borderRadius:6, color:C.orange, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                              Resend
                            </button>
                          )}
                          {!isMe && u.status === "active" && (
                            <button onClick={()=>handleDisable(u)}
                              style={{ padding:"5px 10px", background:"none", border:`1px solid ${C.border}`, borderRadius:6, color:C.muted, fontSize:11, fontWeight:600, cursor:"pointer" }}>
                              Disable
                            </button>
                          )}
                          {!isMe && u.status === "disabled" && (
                            <button onClick={()=>handleEnable(u)}
                              style={{ padding:"5px 10px", background:C.greenSoft, border:`1px solid ${C.green}44`, borderRadius:6, color:C.green, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                              Enable
                            </button>
                          )}
                          {!isMe && (
                            <button onClick={()=>handleDelete(u)}
                              style={{ padding:"5px 10px", background:C.redSoft, border:`1px solid ${C.red}33`, borderRadius:6, color:C.red, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── INVITE MODAL ───────────────────────────
function InviteModal({ C, onClose, onInvite }) {
  const [email, setEmail] = useState("");
  const [role, setRole]   = useState(ROLES.SUPERVISOR);
  const [busy, setBusy]   = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    const ok = await onInvite(email.trim(), role);
    setBusy(false);
    if (ok) onClose();
  };

  const inp = { width:"100%", padding:"10px 13px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:9, color:C.text, fontSize:13, outline:"none", boxSizing:"border-box" };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()}
        style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"24px 26px", width:"100%", maxWidth:440 }}>
        <div style={{ fontSize:17, fontWeight:800, color:C.text, marginBottom:6 }}>Invite User</div>
        <div style={{ fontSize:12, color:C.muted, marginBottom:18 }}>They'll get an email with a link to set their password.</div>

        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, fontWeight:600, color:C.muted, marginBottom:5, textTransform:"uppercase" }}>Email</div>
          <input type="email" autoFocus value={email} onChange={e=>setEmail(e.target.value)} placeholder="person@example.com" style={inp} required />
        </div>

        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:11, fontWeight:600, color:C.muted, marginBottom:5, textTransform:"uppercase" }}>Role</div>
          <select value={role} onChange={e=>setRole(e.target.value)} style={{ ...inp, cursor:"pointer" }}>
            <option value={ROLES.SUPER_ADMIN}>Super Admin — full access incl. user management</option>
            <option value={ROLES.EVENT_ADMIN}>Event Admin — manage events, volunteers, duties</option>
            <option value={ROLES.SUPERVISOR}>Supervisor — assign duties, view data, reports</option>
            <option value={ROLES.VOLUNTEER}>Volunteer — view own duties only</option>
          </select>
        </div>

        <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
          <button type="button" onClick={onClose}
            style={{ padding:"9px 16px", background:"none", border:`1px solid ${C.border}`, borderRadius:8, color:C.muted, fontSize:13, cursor:"pointer" }}>
            Cancel
          </button>
          <button type="submit" disabled={busy || !email}
            style={{ padding:"9px 20px", background:busy?C.border:C.accent, border:"none", borderRadius:8, color:busy?C.muted:"#000", fontSize:13, fontWeight:700, cursor:busy?"not-allowed":"pointer" }}>
            {busy ? "Sending…" : "Send Invitation"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── EDIT MODAL ─────────────────────────────
function EditModal({ user, currentUserId, C, onClose, onSave }) {
  const [name, setName]   = useState(user.name);
  const [role, setRole]   = useState(user.role);
  const [busy, setBusy]   = useState(false);
  const isSelf = user.id === currentUserId;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onSave({ name, role });
    setBusy(false);
  };

  const inp = { width:"100%", padding:"10px 13px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:9, color:C.text, fontSize:13, outline:"none", boxSizing:"border-box" };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()}
        style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"24px 26px", width:"100%", maxWidth:440 }}>
        <div style={{ fontSize:17, fontWeight:800, color:C.text, marginBottom:18 }}>Edit User</div>

        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, fontWeight:600, color:C.muted, marginBottom:5, textTransform:"uppercase" }}>Email (read-only)</div>
          <input value={user.email} readOnly style={{ ...inp, color:C.muted, fontFamily:"monospace" }} />
        </div>

        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, fontWeight:600, color:C.muted, marginBottom:5, textTransform:"uppercase" }}>Name</div>
          <input value={name} onChange={e=>setName(e.target.value)} style={inp} />
        </div>

        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:11, fontWeight:600, color:C.muted, marginBottom:5, textTransform:"uppercase" }}>Role {isSelf && <span style={{color:C.orange}}>(cannot change own role)</span>}</div>
          <select value={role} onChange={e=>setRole(e.target.value)} disabled={isSelf} style={{ ...inp, cursor:isSelf?"not-allowed":"pointer", opacity:isSelf?0.6:1 }}>
            <option value={ROLES.SUPER_ADMIN}>Super Admin</option>
            <option value={ROLES.EVENT_ADMIN}>Event Admin</option>
            <option value={ROLES.SUPERVISOR}>Supervisor</option>
            <option value={ROLES.VOLUNTEER}>Volunteer</option>
          </select>
        </div>

        <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
          <button type="button" onClick={onClose}
            style={{ padding:"9px 16px", background:"none", border:`1px solid ${C.border}`, borderRadius:8, color:C.muted, fontSize:13, cursor:"pointer" }}>
            Cancel
          </button>
          <button type="submit" disabled={busy}
            style={{ padding:"9px 20px", background:busy?C.border:C.accent, border:"none", borderRadius:8, color:busy?C.muted:"#000", fontSize:13, fontWeight:700, cursor:busy?"not-allowed":"pointer" }}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── INVITE RESULT MODAL ────────────────────
function InviteResultModal({ data, C, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(data.inviteLink).then(() => {
      setCopied(true); setTimeout(()=>setCopied(false), 2000);
    });
  };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}>
      <div onClick={e=>e.stopPropagation()}
        style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"24px 26px", width:"100%", maxWidth:520 }}>
        <div style={{ fontSize:17, fontWeight:800, color:C.text, marginBottom:6 }}>
          {data.emailSent ? "✅ Invitation sent" : "📋 Invitation created"}
        </div>
        <div style={{ fontSize:13, color:C.muted, marginBottom:16 }}>
          {data.emailSent
            ? `Email sent to ${data.email}. The link expires in 72 hours.`
            : `Email isn't configured on this server. Copy the link below and share it manually with ${data.email}.`}
        </div>

        <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:9, padding:"10px 12px", marginBottom:14, fontSize:11, fontFamily:"monospace", wordBreak:"break-all", color:C.text }}>
          {data.inviteLink}
        </div>

        <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
          <button onClick={copy}
            style={{ padding:"9px 16px", background:copied?C.greenSoft:C.accentSoft, border:`1px solid ${copied?C.green:C.accent}44`, borderRadius:8, color:copied?C.green:C.accent, fontSize:13, fontWeight:700, cursor:"pointer" }}>
            {copied ? "✓ Copied!" : "Copy Link"}
          </button>
          <button onClick={onClose}
            style={{ padding:"9px 20px", background:C.accent, border:"none", borderRadius:8, color:"#000", fontSize:13, fontWeight:700, cursor:"pointer" }}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}